import React, { useState, useEffect } from "react";
import Button from "apollo-react/components/Button";
import Typography from "apollo-react/components/Typography";
import Box from "apollo-react/components/Box";
import styles from "./ActionButtons/ActionButtons.module.css";
import { useCommitteeReviewLogic } from "../../../hooks/useCommitteeReviewLogic";
import { useRequestComprehensiveDetails } from "../../../hooks/useRequestComprehensiveDetails";
import RejectionReasonModal from "./RejectionReasonModal";
import ApprovalConfirmationModal from "./ApprovalConfirmationModal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CATEGORIES } from "../../../constants/categories";
import { getCurrentUser as getCurrentUserAdmin } from "../../Admin/services/authService";
import { getCurrentUser as getCurrentUserGuest } from "../../Guest/services/authService";

const CommitteeReviewButtons = ({ requestProgressId, persona = "customer", fileUploadTrigger }) => {
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [missingFileMessage, setMissingFileMessage] = useState("");
  const navigate = useNavigate();
  
  const { requestDetails } = useRequestComprehensiveDetails(requestProgressId, persona, fileUploadTrigger);
  
  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = persona === "customer" ? await getCurrentUserGuest() : await getCurrentUserAdmin();
        setCurrentUser(userData);
      } catch (error) {
        console.error("Failed to fetch user", error);
      }
    };
    fetchUser();
  }, [persona]);
  
  // Check for required files based on user role
  useEffect(() => {
    if (!currentUser || !requestDetails) return;
    
    const userCategory = currentUser.end_users_category?.name;
    
    if (userCategory === CATEGORIES.CONTROL_LAB_VALIDATION) {
      if (!requestDetails.request?.labFile) {
        setMissingFileMessage("Laboratory file is required before you can review this request.");
      } else {
        setMissingFileMessage("");
      }
    } else if (userCategory === CATEGORIES.INSPECTION_DEPARTMENT) {
      if (!requestDetails.request?.inspectionFile) {
        setMissingFileMessage("Inspection file is required before you can review this request.");
      } else {
        setMissingFileMessage("");
      }
    } else {
      setMissingFileMessage("");
    }
  }, [currentUser, requestDetails]);
  
  const {
    handleApprove,
    handleDecline,
    isProcessing,
    isApproved,
    isDeclined,
    loadingReviews,
    allMembersFinished
  } = useCommitteeReviewLogic(requestProgressId, persona);

  const handleApproveClick = () => {
    setShowApprovalModal(true);
  };

  const handleRejectClick = () => {
    setShowRejectionModal(true);
  };

  const handleConfirmRejection = async (rejectionReason) => {
    setShowRejectionModal(false);
    await handleDecline(rejectionReason);
    toast.success("Review submitted successfully!", {
      position: "top-center",
    });
    navigate("/customer/assessment-review");
  };

  const handleConfirmApproval = async () => {
    setShowApprovalModal(false);
    await handleApprove();
    toast.success("Review submitted successfully!", {
      position: "top-center",
    });
    navigate("/customer/assessment-review");
  };

  const isDisabled = isProcessing || isApproved || isDeclined || loadingReviews || allMembersFinished || !!missingFileMessage;

  return (
    <>
      
      <div className={styles.actionButtonsContainer}>
      {missingFileMessage && (
        <Box sx={{ 
          padding: 1, 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffecb5', 
          borderRadius: 1 
        }}>
          <Typography variant="body2" color="#856404">
            ⚠️ {missingFileMessage}
          </Typography>
        </Box>
      )}
        <Button
          variant="primary"
          onClick={handleApproveClick}
          disabled={isDisabled}
          style={{ minWidth: "157px", minHeight: "40px" }}
        >
          {isApproved ? "Approved" : "Approve"}
        </Button>
        <Button
          variant="primary"
          onClick={handleRejectClick}
          disabled={isDisabled}
          style={{ marginLeft: 8, minWidth: "157px", minHeight: "40px" }}
        >
          {isDeclined ? "Rejected" : "Reject"}
        </Button>
        {isProcessing && <p>Processing...</p>}
      </div>
      
      <RejectionReasonModal
        isOpen={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        onConfirm={handleConfirmRejection}
        isProcessing={isProcessing}
      />
      
      <ApprovalConfirmationModal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        onConfirm={handleConfirmApproval}
        isProcessing={isProcessing}
      />
    </>
  );
};

export default CommitteeReviewButtons; 